import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime';
import * as Sentry from '@sentry/react';

/**
 * Minimal valid federation manifest used when the real remote entry cannot be fetched
 * (RUNTIME-003). Downstream `onLoad` still fails and returns `default: null`.
 */
export const createFallbackManifest = (id: string) => ({
  id,
  name: id,
  metaData: {
    name: id,
    type: 'app',
    buildInfo: {
      buildVersion: 'local',
      buildName: id,
    },
    remoteEntry: {
      name: 'remoteEntry.js',
      path: '',
      type: 'global',
    },
    types: {
      path: '',
      name: '',
      zip: '@mf-types.zip',
      api: '@mf-types.d.ts',
    },
    globalName: id,
    pluginVersion: '1',
    publicPath: '/',
  },
  shared: [],
  remotes: [],
  exposes: [],
});

/** Error.message/stack are non-enumerable — JSON.stringify(error) yields `{}`. */
export const serializeLoadRemoteError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (error == null) {
    return undefined;
  }

  try {
    return { message: String(error), raw: error };
  } catch {
    return { message: 'Unknown error' };
  }
};

/**
 * Runtime plugin that recovers when a remote is unreachable.
 * Register via federation `runtimePlugins` (default export required by MF).
 *
 * - `afterResolve`: stub manifest (RUNTIME-003)
 * - `onLoad` / `beforeLoadShare`: `default: null` so host can fall back
 */
export const createErrorLoadRemotePlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'multiplier-error-load-remote',
  async errorLoadRemote(args) {
    const lifecycle = String(args.lifecycle ?? 'unknown');
    const serializedError = serializeLoadRemoteError(args.error);

    Sentry.addBreadcrumb({
      category: 'mfe.error-load-remote',
      level: 'warning',
      message: 'createErrorLoadRemotePlugin',
      data: {
        id: args.id,
        lifecycle,
        from: args.from,
        error: serializedError,
      },
    });

    switch (lifecycle) {
      case 'afterResolve':
        return createFallbackManifest(args.id ?? 'unknown-remote');
      case 'onLoad':
      case 'beforeLoadShare':
        return () => ({
          __esModule: true,
          default: null,
        });
      default:
        return undefined;
    }
  },
});

export default createErrorLoadRemotePlugin;
